import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Checkbox,
  Button,
  Grid,
} from '@mui/material';
import { CheckCircle2, AlertTriangle, AlertCircle, Check } from 'lucide-react';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { NameEmailAvatar } from '../../../../../../../ui';
import { IOfferDetails } from '../Interfaces';

interface ICandidateDetails {
  selectedCandidate: IOfferDetails;
  isUpdatingStatus: boolean;
  updateCandidateOfferStatus: (candidateId: string, status: string) => void;
}

export const CandidateDetails: React.FC<ICandidateDetails> = (props) => {
  const { selectedCandidate, isUpdatingStatus, updateCandidateOfferStatus } = props;
  const { t } = useTranslation(NS.adminPages);
  const CD = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews.offer.candidateDetails;
  const [isConfirmed, setIsConfirmed] = useState(false);

  const userDetails = selectedCandidate?.interviewApplicant?.user || {
    firstName: '',
    lastName: '',
    email: '',
  };

  const status = selectedCandidate?.applicant_status || '';

  const getStatusColor = (currentStatus: string) => {
    if (currentStatus.includes('Accepted')) return 'success';
    if (currentStatus.includes('Rejected') || currentStatus.includes('Declined')) return 'error';
    if (currentStatus.includes('Withheld')) return 'warning';
    return 'primary';
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        borderRadius: 4,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t(CD.title)}
        </Typography>
      </Box>

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Grid container spacing={3}>
          {/* Left column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ mb: 1 }}>
                <NameEmailAvatar
                  name={`${userDetails.firstName || ''} ${userDetails.lastName || ''}`}
                  email={userDetails.email || ''}
                  avatar=""
                  avatarWidth="48px"
                  avatarHeight="48px"
                  nameFontSize="1.1rem"
                  emailFontSize="0.9rem"
                  applicantStatus={selectedCandidate.applicant_status}
                />
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t(CD.status)}
                </Typography>
                <Chip
                  label={selectedCandidate.applicant_status || 'Unknown'}
                  size="small"
                  color={getStatusColor(selectedCandidate.applicant_status || '') as any}
                  sx={{ mt: 0.5, fontWeight: 600 }}
                />
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t(CD.interviewType)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {selectedCandidate?.interviewApplicant?.interviewType || 'N/A'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t(CD.interviewDate)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {selectedCandidate?.interviewApplicant?.interviewStartTime
                    ? new Date(
                        selectedCandidate.interviewApplicant.interviewStartTime * 1000,
                      ).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t(CD.averageScore)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                  <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 2, height: 8, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 2,
                        width: `${selectedCandidate?.interviewApplicant?.averageScore || 0}%`,
                        bgcolor:
                          (selectedCandidate?.interviewApplicant?.averageScore || 0) >= 70
                            ? 'success.main'
                            : (selectedCandidate?.interviewApplicant?.averageScore || 0) >= 50
                              ? 'warning.main'
                              : 'error.main',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', minWidth: 40 }}>
                    {selectedCandidate?.interviewApplicant?.averageScore || 0}%
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t(CD.aiScore)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                  <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 2, height: 8, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 2,
                        width: `${selectedCandidate?.interviewApplicant?.aiOverallScore?.score || 0}%`,
                        bgcolor:
                          (selectedCandidate?.interviewApplicant?.aiOverallScore?.score || 0) >= 70
                            ? 'success.main'
                            : (selectedCandidate?.interviewApplicant?.aiOverallScore?.score || 0) >= 50
                              ? 'warning.main'
                              : 'error.main',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', minWidth: 40 }}>
                    {selectedCandidate?.interviewApplicant?.aiOverallScore?.score || 0}%
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t(CD.location)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {selectedCandidate?.interviewApplicant?.inPersonAddress || 'Virtual'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t(CD.meetingLink)}
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {selectedCandidate?.interviewApplicant?.meetingLink || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Skills section */}
        <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
            {t(CD.skillsAssessment)}
          </Typography>
          <Grid container spacing={2}>
            {([
              { key: t(CD.skills.technicalSkills), data: selectedCandidate?.interviewApplicant?.technicalSkills },
              { key: t(CD.skills.roleExperience), data: selectedCandidate?.interviewApplicant?.roleExperience },
              { key: t(CD.skills.industrySkills), data: selectedCandidate?.interviewApplicant?.industrySkills },
              { key: t(CD.skills.productKnowledge), data: selectedCandidate?.interviewApplicant?.productKnowledge },
              { key: t(CD.skills.communication), data: selectedCandidate?.interviewApplicant?.communication },
              { key: t(CD.skills.problemSolving), data: selectedCandidate?.interviewApplicant?.problemSolving },
              { key: t(CD.skills.leadership), data: selectedCandidate?.interviewApplicant?.leadership },
              { key: t(CD.skills.teamCollaboration), data: selectedCandidate?.interviewApplicant?.teamCollaboration },
            ] as Array<{ key: string; data: any }>).map(({ key: skillName, data: skillData }) => (
              <Grid item xs={6} md={3} key={skillName}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
                    {skillName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 6, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 1,
                          width: `${skillData?.score || 0}%`,
                          bgcolor:
                            (skillData?.score || 0) >= 70
                              ? 'success.main'
                              : (skillData?.score || 0) >= 50
                                ? 'warning.main'
                                : 'error.main',
                        }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {skillData?.score || 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Actions section */}
        <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {(() => {
            if (status === 'Interview' || !status || status === 'Offer') {
              return (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: 'error.50',
                    borderColor: 'error.light',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {t(CD.makeFinalDecision)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t(CD.finalDecisionSubtitle)}{' '}
                      <Box component="span" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {userDetails?.firstName || t(CD.unknown)}
                      </Box>
                    </Typography>
                  </Box>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Checkbox
                      checked={isConfirmed}
                      onChange={(e) => setIsConfirmed(e.target.checked)}
                      color="error"
                    />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                      {t(CD.confirmCheckbox)}
                    </Typography>
                  </Paper>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="error"
                      disabled={!isConfirmed || isUpdatingStatus}
                      onClick={() => {
                        if (!isConfirmed) {
                          toast.warning(t(CD.pleaseConfirmFirst), {
                            position: 'bottom-center',
                          });
                          return;
                        }
                        updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Declined');
                      }}
                      sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
                    >
                      {t(CD.rejectCandidate)}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={!isConfirmed || isUpdatingStatus}
                      onClick={() => {
                        if (!isConfirmed) {
                          toast.warning(t(CD.pleaseConfirmFirst), {
                            position: 'bottom-center',
                          });
                          return;
                        }
                        updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Extended');
                      }}
                      sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
                    >
                      {t(CD.sendOffer)}
                    </Button>
                  </Box>
                </Paper>
              );
            }

            if (status === 'Offer-Extended') {
              return (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: 'success.50',
                    borderColor: 'success.light',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: 'success.100',
                        color: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle2 size={24} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {t(CD.offerExtended)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(CD.offerSentTo)}{' '}
                        <Box component="span" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {userDetails?.firstName || t(CD.unknown)}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>

                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t(CD.candidateReviewing)}
                    </Typography>
                  </Paper>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={isUpdatingStatus}
                      onClick={() =>
                        updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Declined')
                      }
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {t(CD.withdrawOffer)}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={isUpdatingStatus}
                      onClick={() =>
                        updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Accepted')
                      }
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {t(CD.candidateAcceptedOffer)}
                    </Button>
                  </Box>
                </Paper>
              );
            }

            if (status === 'Offer-Accepted') {
              return (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: 'success.50',
                    borderColor: 'success.light',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: 'success.100',
                        color: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Check size={24} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {t(CD.offerAccepted)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {userDetails?.firstName || t(CD.unknown)}
                        </Box>{' '}
                        {t(CD.hasAccepted)}
                      </Typography>
                    </Box>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t(CD.congratulations)}
                    </Typography>
                  </Paper>
                </Paper>
              );
            }

            if (status === 'Offer-Declined') {
              return (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: 'warning.50',
                    borderColor: 'warning.light',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: 'warning.100',
                        color: 'warning.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AlertTriangle size={24} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {t(CD.offerRejectedDeclined)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <Box component="span" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                          {userDetails?.firstName || t(CD.unknown)}
                        </Box>{' '}
                        {status === 'Offer-Declined' ? t(CD.wasRejected) : t(CD.wasDeclined)}
                      </Typography>
                    </Box>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="body2" color="text.secondary">
                      {status === 'Offer-Declined' ? t(CD.notSelectedMessage) : t(CD.declinedMessage)}
                    </Typography>
                  </Paper>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={isUpdatingStatus}
                      onClick={() =>
                        updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Extended')
                      }
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {t(CD.extendRevisedOffer)}
                    </Button>
                  </Box>
                </Paper>
              );
            }

            if (status === 'Offer-Withheld') {
              return (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    bgcolor: 'warning.50',
                    borderColor: 'warning.light',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: 'warning.100',
                        color: 'warning.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AlertCircle size={24} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {t(CD.offerWithheld)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(CD.offerOnHold)}{' '}
                        <Box component="span" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                          {userDetails?.firstName || t(CD.unknown)}
                        </Box>
                      </Typography>
                    </Box>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t(CD.withheldMessage)}
                    </Typography>
                  </Paper>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={isUpdatingStatus}
                      onClick={() =>
                        updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Declined')
                      }
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {t(CD.rejectCandidate)}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      disabled={isUpdatingStatus}
                      onClick={() =>
                        updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Extended')
                      }
                      sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                      {t(CD.extendOffer)}
                    </Button>
                  </Box>
                </Paper>
              );
            }

            return (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  disabled={isUpdatingStatus || selectedCandidate.applicant_status === 'Offer-Accepted'}
                  onClick={() =>
                    updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Accepted')
                  }
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {t(CD.acceptOffer)}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={isUpdatingStatus || selectedCandidate.applicant_status === 'Offer-Declined'}
                  onClick={() =>
                    updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Declined')
                  }
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {t(CD.rejectOffer)}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={isUpdatingStatus || selectedCandidate.applicant_status === 'Offer-Withheld'}
                  onClick={() =>
                    updateCandidateOfferStatus(selectedCandidate.jobApplicantId, 'Offer-Withheld')
                  }
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {t(CD.withholdOffer)}
                </Button>
              </Box>
            );
          })()}
        </Box>
      </Box>
    </Paper>
  );
};

export default CandidateDetails;
